/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Button, Input, Select, DatePicker, Modal, Spin, message, Tooltip, Badge } from 'antd';
import {
    SearchOutlined, ReloadOutlined, DownloadOutlined,
    WarningOutlined, CheckCircleOutlined, FileTextOutlined,
    LockOutlined, UserOutlined, DeleteOutlined,
    RiseOutlined, FallOutlined, ClockCircleOutlined,
    FireOutlined, EyeOutlined, FilterOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import FleetService from '../../services/FleetService';

const { Option }      = Select;
const { RangePicker } = DatePicker;

// ─── Config ───────────────────────────────────────────────────────────────────
const ACTION_CFG = {
    CREATE:             { color:'#389e0d', bg:'#f6ffed', border:'#b7eb8f', icon:<CheckCircleOutlined />, label:'Created'           },
    UPDATE:             { color:'#1890ff', bg:'#e6f4ff', border:'#91caff', icon:<FileTextOutlined />,    label:'Updated'           },
    DELETE:             { color:'#cf1322', bg:'#fff1f0', border:'#ffa39e', icon:<DeleteOutlined />,      label:'Deleted'           },
    APPROVE_HOD:        { color:'#1d4ed8', bg:'#eff6ff', border:'#bfdbfe', icon:<CheckCircleOutlined />, label:'HOD Approved'      },
    APPROVE_DIRECTOR:   { color:'#6d28d9', bg:'#f5f3ff', border:'#ddd6fe', icon:<CheckCircleOutlined />, label:'Director Approved' },
    DISPOSE:            { color:'#7f1d1d', bg:'#fef2f2', border:'#fecaca', icon:<WarningOutlined />,     label:'Disposed'          },
    ASSIGN:             { color:'#0891b2', bg:'#ecfeff', border:'#a5f3fc', icon:<UserOutlined />,        label:'Assigned'          },
    END_ASSIGN:         { color:'#d97706', bg:'#fffbeb', border:'#fde68a', icon:<UserOutlined />,        label:'Assignment Ended'  },
    RETURN:             { color:'#059669', bg:'#ecfdf5', border:'#a7f3d0', icon:<CheckCircleOutlined />, label:'Returned'          },
    LOGIN:              { color:'#6b7280', bg:'#f9fafb', border:'#e5e7eb', icon:<LockOutlined />,        label:'Login'             },
    PERMISSION_CHANGE:  { color:'#7c3aed', bg:'#f5f3ff', border:'#ddd6fe', icon:<LockOutlined />,        label:'Permission Change' },
    ROLE_CHANGE:        { color:'#b45309', bg:'#fffbeb', border:'#fde68a', icon:<LockOutlined />,        label:'Role Changed'      },
};
const SEV_CFG = {
    critical: { color:'#cf1322', bg:'#fff1f0', border:'#ffa39e', dot:'#f5222d' },
    high:     { color:'#d46b08', bg:'#fff7e6', border:'#ffd591', dot:'#fa8c16' },
    medium:   { color:'#0958d9', bg:'#e6f4ff', border:'#91caff', dot:'#1677ff' },
    low:      { color:'#389e0d', bg:'#f6ffed', border:'#b7eb8f', dot:'#52c41a' },
};

// Anomaly detection rules — flags suspicious patterns in the log set
const ANOMALY_RULES = [
    {
        id: 'burst_deletes',
        label: 'Deletion burst',
        desc: (n, u) => `${n} deletions by ${u} in a short window — possible bulk purge`,
        check: (logs) => {
            const byUser = {};
            logs.filter(l => l.action === 'DELETE').forEach(l => {
                byUser[l.user_name] = (byUser[l.user_name] || 0) + 1;
            });
            return Object.entries(byUser)
                .filter(([, n]) => n >= 3)
                .map(([u, n]) => ({ severity:'critical', user: u, count: n, desc: `${n} deletions by ${u}` }));
        },
    },
    {
        id: 'after_hours',
        label: 'After-hours activity',
        desc: () => 'Critical actions performed outside 06:00–18:00',
        check: (logs) => {
            const hits = logs.filter(l => {
                const h = moment(l.created_at).hour();
                return (h < 6 || h > 18) && ['DELETE','APPROVE_HOD','APPROVE_DIRECTOR','DISPOSE'].includes(l.action);
            });
            return hits.length > 0 ? [{ severity:'high', desc: `${hits.length} critical action(s) after hours`, items: hits.slice(0,5) }] : [];
        },
    },
    {
        id: 'repeated_role_changes',
        label: 'Repeated permission changes',
        desc: () => 'Role or permission changed 2+ times in this period',
        check: (logs) => {
            const roleChanges = logs.filter(l => ['ROLE_CHANGE','PERMISSION_CHANGE'].includes(l.action) || l.module === 'Users/Role');
            const byUser = {};
            roleChanges.forEach(l => { byUser[l.user_name] = (byUser[l.user_name] || 0) + 1; });
            return Object.entries(byUser)
                .filter(([, n]) => n >= 2)
                .map(([u, n]) => ({ severity:'high', desc: `${n} role/permission changes by ${u}` }));
        },
    },
    {
        id: 'disposal_no_approval',
        label: 'Disposal without approval trail',
        desc: () => 'Vehicle marked Disposed without HOD or Director approval logged',
        check: (logs) => {
            const disposed  = logs.filter(l => l.action === 'DISPOSE').map(l => l.record_id);
            const hodIds    = new Set(logs.filter(l => l.action === 'APPROVE_HOD').map(l => l.record_id));
            const dirIds    = new Set(logs.filter(l => l.action === 'APPROVE_DIRECTOR').map(l => l.record_id));
            const missing   = disposed.filter(id => !hodIds.has(id) && !dirIds.has(id));
            return missing.length > 0 ? [{ severity:'critical', desc: `${missing.length} disposal(s) with no approval in current log view` }] : [];
        },
    },
    {
        id: 'high_ip_variety',
        label: 'Multiple IPs — same user',
        desc: () => 'User logged in or made changes from 3+ different IP addresses',
        check: (logs) => {
            const byUser = {};
            logs.forEach(l => {
                if (!l.ip_address) return;
                if (!byUser[l.user_name]) byUser[l.user_name] = new Set();
                byUser[l.user_name].add(l.ip_address);
            });
            return Object.entries(byUser)
                .filter(([, ips]) => ips.size >= 3)
                .map(([u, ips]) => ({ severity:'medium', desc: `${u} acted from ${ips.size} IPs: ${[...ips].join(', ')}` }));
        },
    },
];

const fmtDT   = (v) => v ? moment(v).format('DD MMM YYYY HH:mm') : '—';
const toN     = (v) => Number(v || 0);

// ─── Small components ─────────────────────────────────────────────────────────
function ActionBadge({ action }) {
    const cfg = ACTION_CFG[action] || { color:'#595959', bg:'#f5f5f5', border:'#d9d9d9', icon:null, label: action };
    return (
        <span style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'2px 9px',
            borderRadius:10, fontSize:11, fontWeight:600, color:cfg.color,
            background:cfg.bg, border:`1px solid ${cfg.border}`, whiteSpace:'nowrap' }}>
            {cfg.icon} {cfg.label}
        </span>
    );
}
function SevDot({ severity }) {
    const cfg = SEV_CFG[severity] || SEV_CFG.medium;
    return (
        <span style={{ display:'inline-flex', alignItems:'center', gap:5, fontSize:11, fontWeight:600, color:cfg.color }}>
            <span style={{ width:8, height:8, borderRadius:'50%', background:cfg.dot, flexShrink:0, display:'inline-block' }} />
            {severity?.charAt(0).toUpperCase() + severity?.slice(1)}
        </span>
    );
}

// ─── Anomaly panel ────────────────────────────────────────────────────────────
function AnomalyPanel({ logs }) {
    const findings = useMemo(() => {
        const all = [];
        ANOMALY_RULES.forEach(rule => {
            const hits = rule.check(logs);
            hits.forEach(h => all.push({ ...h, ruleLabel: rule.label }));
        });
        return all.sort((a, b) => {
            const order = { critical:0, high:1, medium:2, low:3 };
            return (order[a.severity]||2) - (order[b.severity]||2);
        });
    }, [logs]);

    if (!findings.length) return (
        <div style={{ display:'flex', alignItems:'center', gap:8, background:'#f6ffed',
            border:'1px solid #b7eb8f', borderRadius:8, padding:'10px 14px', marginBottom:16, fontSize:12 }}>
            <CheckCircleOutlined style={{ color:'#389e0d' }} />
            <span style={{ color:'#389e0d', fontWeight:500 }}>No anomalies detected in current view</span>
        </div>
    );

    return (
        <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:11, fontWeight:600, color:'#cf1322', textTransform:'uppercase',
                letterSpacing:'.06em', marginBottom:8, display:'flex', alignItems:'center', gap:6 }}>
                <FireOutlined /> Anomalies detected — {findings.length} flag{findings.length !== 1 ? 's' : ''}
            </div>
            {findings.map((f, i) => {
                const cfg = SEV_CFG[f.severity] || SEV_CFG.medium;
                return (
                    <div key={i} style={{ display:'flex', gap:10, alignItems:'flex-start',
                        background:cfg.bg, border:`1px solid ${cfg.border}`,
                        borderLeft:`3px solid ${cfg.dot}`, borderRadius:6,
                        padding:'8px 12px', marginBottom:6, fontSize:12 }}>
                        <span style={{ width:8, height:8, borderRadius:'50%', background:cfg.dot,
                            flexShrink:0, marginTop:4 }} />
                        <div>
                            <span style={{ fontWeight:600, color:cfg.color }}>{f.ruleLabel}: </span>
                            <span style={{ color:'#444' }}>{f.desc}</span>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

// ─── Mini Dashboard ───────────────────────────────────────────────────────────
function MiniDashboard({ logs, total, prevLogs }) {
    const stats = useMemo(() => {
        const critCount     = logs.filter(l => l.severity === 'critical').length;
        const delCount      = logs.filter(l => l.action === 'DELETE').length;
        const approvals     = logs.filter(l => ['APPROVE_HOD','APPROVE_DIRECTOR','DISPOSE'].includes(l.action)).length;
        const roleChanges   = logs.filter(l => l.module === 'Users/Role' || l.module === 'Users/User').length;
        const uniqueUsers   = new Set(logs.map(l => l.user_name).filter(Boolean)).size;
        const uniqueIPs     = new Set(logs.map(l => l.ip_address).filter(Boolean)).size;

        // Activity by hour (bar sparkline)
        const byHour = Array(24).fill(0);
        logs.forEach(l => { if (l.created_at) byHour[moment(l.created_at).hour()]++; });
        const peakHour = byHour.indexOf(Math.max(...byHour));

        // Top actors
        const byUser = {};
        logs.forEach(l => { byUser[l.user_name] = (byUser[l.user_name]||0) + 1; });
        const topUsers = Object.entries(byUser).sort((a,b)=>b[1]-a[1]).slice(0,4);

        // Action breakdown
        const byAction = {};
        logs.forEach(l => { byAction[l.action] = (byAction[l.action]||0) + 1; });
        const topActions = Object.entries(byAction).sort((a,b)=>b[1]-a[1]).slice(0,5);

        // Severity breakdown
        const bySev = { critical:0, high:0, medium:0, low:0 };
        logs.forEach(l => { if (bySev[l.severity] !== undefined) bySev[l.severity]++; });

        return { critCount, delCount, approvals, roleChanges, uniqueUsers, uniqueIPs,
                 byHour, peakHour, topUsers, topActions, bySev };
    }, [logs]);

    const KPI = ({ label, value, color, icon, sub }) => (
        <div style={{ background:'#fff', border:'1px solid #e8e8e8', borderRadius:8,
            padding:'12px 14px', borderTop:`3px solid ${color}` }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                <div style={{ fontSize:10, color:'#888', marginBottom:4 }}>{label}</div>
                <span style={{ color, fontSize:16, opacity:.6 }}>{icon}</span>
            </div>
            <div style={{ fontSize:24, fontWeight:700, color, lineHeight:1 }}>{value}</div>
            {sub && <div style={{ fontSize:10, color:'#aaa', marginTop:3 }}>{sub}</div>}
        </div>
    );

    const maxBar = Math.max(...stats.byHour, 1);

    return (
        <div style={{ marginBottom:20 }}>
            {/* KPI row */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(6,1fr)', gap:10, marginBottom:14 }}>
                <KPI label="Total Records"      value={total.toLocaleString()}  color="#1a3a5c" icon={<FileTextOutlined />} sub="in selected period" />
                <KPI label="Critical Actions"   value={stats.critCount}         color="#cf1322" icon={<WarningOutlined />}  sub="require review" />
                <KPI label="Deletions"          value={stats.delCount}          color="#f5222d" icon={<DeleteOutlined />}   sub="records removed" />
                <KPI label="Approvals"          value={stats.approvals}         color="#1d4ed8" icon={<CheckCircleOutlined />} sub="HOD + Director" />
                <KPI label="Unique Users"       value={stats.uniqueUsers}       color="#389e0d" icon={<UserOutlined />}     sub="active in period" />
                <KPI label="Permission Changes" value={stats.roleChanges}       color="#7c3aed" icon={<LockOutlined />}     sub="roles / users" />
            </div>

            {/* Second row: activity chart + top users + action breakdown */}
            <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr', gap:10 }}>

                {/* Hourly activity sparkline */}
                <div style={{ background:'#fff', border:'1px solid #e8e8e8', borderRadius:8, padding:'12px 14px' }}>
                    <div style={{ fontSize:11, fontWeight:600, color:'#595959', marginBottom:10,
                        display:'flex', justifyContent:'space-between' }}>
                        <span>Activity by hour</span>
                        <span style={{ fontWeight:400, color:'#aaa' }}>
                            Peak: {stats.peakHour.toString().padStart(2,'0')}:00 ({stats.byHour[stats.peakHour]} events)
                        </span>
                    </div>
                    <div style={{ display:'flex', alignItems:'flex-end', gap:2, height:52 }}>
                        {stats.byHour.map((v, h) => {
                            const pct  = maxBar > 0 ? v/maxBar : 0;
                            const isPeak = h === stats.peakHour && v > 0;
                            const isAfterHours = (h < 6 || h > 18) && v > 0;
                            return (
                                <Tooltip key={h} title={`${h.toString().padStart(2,'0')}:00 — ${v} events`}>
                                    <div style={{
                                        flex:1, height: `${Math.max(pct*100, v>0?8:2)}%`,
                                        background: isAfterHours ? '#cf1322' : isPeak ? '#fa8c16' : '#1a3a5c',
                                        borderRadius:'2px 2px 0 0', opacity: v===0 ? 0.15 : 0.85,
                                        cursor:'default', transition:'opacity .15s',
                                        minHeight: v > 0 ? 4 : 1,
                                    }} />
                                </Tooltip>
                            );
                        })}
                    </div>
                    <div style={{ display:'flex', justifyContent:'space-between',
                        fontSize:9, color:'#ccc', marginTop:3 }}>
                        {['00','06','12','18','23'].map(h => <span key={h}>{h}h</span>)}
                    </div>
                    <div style={{ display:'flex', gap:12, marginTop:6, fontSize:10, color:'#888' }}>
                        <span><span style={{ color:'#cf1322' }}>■</span> After-hours</span>
                        <span><span style={{ color:'#fa8c16' }}>■</span> Peak hour</span>
                        <span><span style={{ color:'#1a3a5c' }}>■</span> Normal</span>
                    </div>
                </div>

                {/* Top actors */}
                <div style={{ background:'#fff', border:'1px solid #e8e8e8', borderRadius:8, padding:'12px 14px' }}>
                    <div style={{ fontSize:11, fontWeight:600, color:'#595959', marginBottom:10 }}>Top active users</div>
                    {stats.topUsers.length === 0
                        ? <div style={{ fontSize:12, color:'#ccc' }}>No data</div>
                        : stats.topUsers.map(([user, cnt], i) => {
                            const pct = stats.topUsers[0][1] > 0 ? cnt/stats.topUsers[0][1] : 0;
                            return (
                                <div key={user} style={{ marginBottom:8 }}>
                                    <div style={{ display:'flex', justifyContent:'space-between',
                                        fontSize:11, marginBottom:2 }}>
                                        <span style={{ fontWeight:500, color:'#333', overflow:'hidden',
                                            textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:110 }}>
                                            {user || '—'}
                                        </span>
                                        <span style={{ fontWeight:600, color:'#1a3a5c', flexShrink:0 }}>{cnt}</span>
                                    </div>
                                    <div style={{ height:4, background:'#f0f0f0', borderRadius:2 }}>
                                        <div style={{ height:4, width:`${pct*100}%`,
                                            background:['#1a3a5c','#389e0d','#d46b08','#cf1322'][i]||'#999',
                                            borderRadius:2 }} />
                                    </div>
                                </div>
                            );
                        })
                    }
                </div>

                {/* Severity breakdown */}
                <div style={{ background:'#fff', border:'1px solid #e8e8e8', borderRadius:8, padding:'12px 14px' }}>
                    <div style={{ fontSize:11, fontWeight:600, color:'#595959', marginBottom:10 }}>By severity</div>
                    {Object.entries(stats.bySev).map(([sev, cnt]) => {
                        const cfg   = SEV_CFG[sev];
                        const total = logs.length || 1;
                        return (
                            <div key={sev} style={{ display:'flex', alignItems:'center',
                                gap:8, marginBottom:7 }}>
                                <span style={{ width:8, height:8, borderRadius:'50%',
                                    background:cfg.dot, flexShrink:0 }} />
                                <span style={{ fontSize:11, color:'#595959', flex:1,
                                    textTransform:'capitalize' }}>{sev}</span>
                                <span style={{ fontSize:11, fontWeight:700, color:cfg.color,
                                    minWidth:24, textAlign:'right' }}>{cnt}</span>
                                <div style={{ width:50, height:4, background:'#f0f0f0', borderRadius:2 }}>
                                    <div style={{ height:4, width:`${cnt/total*100}%`,
                                        background:cfg.dot, borderRadius:2 }} />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

// ─── Detail Modal ─────────────────────────────────────────────────────────────
function DetailModal({ record, onClose }) {
    if (!record) return null;
    const cfg = ACTION_CFG[record.action] || {};

    const renderValues = (label, json) => {
        if (!json) return null;
        let obj;
        try { obj = typeof json === 'string' ? JSON.parse(json) : json; } catch { return null; }
        if (!obj || !Object.keys(obj).length) return null;
        return (
            <div style={{ marginBottom:12 }}>
                <div style={{ fontSize:11, fontWeight:600, color:'#888', textTransform:'uppercase',
                    letterSpacing:'.05em', marginBottom:6 }}>{label}</div>
                <div style={{ background:'#fafafa', border:'1px solid #e8e8e8', borderRadius:6,
                    padding:'10px 12px', display:'grid',
                    gridTemplateColumns:'repeat(auto-fill, minmax(200px,1fr))', gap:'6px 12px' }}>
                    {Object.entries(obj).map(([k, v]) => (
                        <div key={k}>
                            <div style={{ fontSize:10, color:'#aaa', fontWeight:500 }}>
                                {k.replace(/_/g,' ').toUpperCase()}
                            </div>
                            <div style={{ fontSize:12, color:'#333', wordBreak:'break-all' }}>
                                {v === null || v === undefined ? '—'
                                    : typeof v === 'object' ? JSON.stringify(v)
                                    : String(v) || '—'}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const renderDiff = () => {
        if (!record.old_values || !record.new_values) return null;
        let oldObj, newObj;
        try { oldObj = JSON.parse(record.old_values); newObj = JSON.parse(record.new_values); }
        catch { return null; }
        const keys    = [...new Set([...Object.keys(oldObj), ...Object.keys(newObj)])];
        const changed = keys.filter(k => String(oldObj[k]||'') !== String(newObj[k]||''));
        if (!changed.length) return null;
        return (
            <div style={{ marginBottom:12 }}>
                <div style={{ fontSize:11, fontWeight:600, color:'#888', textTransform:'uppercase',
                    letterSpacing:'.05em', marginBottom:6 }}>
                    Field changes ({changed.length})
                </div>
                <div style={{ border:'1px solid #e8e8e8', borderRadius:6, overflow:'hidden' }}>
                    <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                        <thead>
                            <tr style={{ background:'#1a3a5c', color:'#fff' }}>
                                {['Field','Before','After'].map(h => (
                                    <th key={h} style={{ padding:'6px 10px', fontWeight:500, fontSize:11, textAlign:'left' }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {changed.map((k, i) => (
                                <tr key={k} style={{ background:i%2===0?'#fff':'#f9f9f9', borderBottom:'1px solid #f0f0f0' }}>
                                    <td style={{ padding:'6px 10px', fontWeight:500, color:'#595959' }}>
                                        {k.replace(/_/g,' ')}
                                    </td>
                                    <td style={{ padding:'6px 10px', color:'#cf1322', background:'#fff1f0', wordBreak:'break-all' }}>
                                        {String(oldObj[k]??'—')}
                                    </td>
                                    <td style={{ padding:'6px 10px', color:'#389e0d', background:'#f6ffed', wordBreak:'break-all' }}>
                                        {String(newObj[k]??'—')}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    return (
        <Modal open={!!record} onCancel={onClose} width={740} destroyOnClose
            footer={<Button onClick={onClose}>Close</Button>}
            title={
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                    <span style={{ color:cfg.color }}>{cfg.icon}</span>
                    <span>Audit Record #{record.id}</span>
                    <ActionBadge action={record.action} />
                    <SevDot severity={record.severity} />
                </div>
            }>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8, marginBottom:14 }}>
                {[
                    ['User',       `${record.user_name} (ID ${record.user_id})`],
                    ['Role',        record.user_role || '—'],
                    ['When',        fmtDT(record.created_at)],
                    ['Module',      record.module],
                    ['Table',       record.table_name],
                    ['Record ID',   record.record_id || '—'],
                    ['IP Address',  record.ip_address || '—'],
                    ['Region',      record.region || '—'],
                    ['Facility',    record.client_id ? `ID ${record.client_id}` : 'National'],
                ].map(([lbl, val]) => (
                    <div key={lbl} style={{ background:'#fafafa', borderRadius:6, padding:'8px 10px' }}>
                        <div style={{ fontSize:10, color:'#aaa', fontWeight:500, marginBottom:2 }}>{lbl}</div>
                        <div style={{ fontSize:12, fontWeight:500, color:'#333' }}>{val}</div>
                    </div>
                ))}
            </div>
            <div style={{ background:'#f0f5ff', border:'1px solid #adc6ff', borderRadius:6,
                padding:'10px 12px', marginBottom:12, fontSize:13, fontWeight:500, color:'#1a3a5c' }}>
                {record.description}
            </div>
            {renderDiff()}
            {!record.old_values && renderValues('Data Submitted', record.new_values)}
            {record.old_values && !record.new_values && renderValues('Record Before Deletion', record.old_values)}
        </Modal>
    );
}

// ─── Quick period presets ─────────────────────────────────────────────────────
const PERIODS = [
    { label:'Today',         days:0,   from:() => moment().format('YYYY-MM-DD'),             to:() => moment().format('YYYY-MM-DD') },
    { label:'Yesterday',     days:1,   from:() => moment().subtract(1,'d').format('YYYY-MM-DD'), to:() => moment().subtract(1,'d').format('YYYY-MM-DD') },
    { label:'Last 7 days',   days:7,   from:() => moment().subtract(6,'d').format('YYYY-MM-DD'), to:() => moment().format('YYYY-MM-DD') },
    { label:'Last 30 days',  days:30,  from:() => moment().subtract(29,'d').format('YYYY-MM-DD'), to:() => moment().format('YYYY-MM-DD') },
    { label:'This month',    days:-1,  from:() => moment().startOf('month').format('YYYY-MM-DD'), to:() => moment().format('YYYY-MM-DD') },
    { label:'Last month',    days:-2,  from:() => moment().subtract(1,'month').startOf('month').format('YYYY-MM-DD'), to:() => moment().subtract(1,'month').endOf('month').format('YYYY-MM-DD') },
    { label:'All time',      days:-99, from:() => '', to:() => '' },
];

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function FleetAuditLog() {
    const [logs,    setLogs]    = useState([]);
    const [total,   setTotal]   = useState(0);
    const [loading, setLoading] = useState(false);
    const [detail,  setDetail]  = useState(null);
    const [page,    setPage]    = useState(1);
    const [ps,      setPs]      = useState(50);
    const [activePeriod, setActivePeriod] = useState('Last 7 days');

    const [filters, setFilters] = useState({
        action:'', severity:'', module:'', user_name:'', k:'',
        from_date: moment().subtract(6,'d').format('YYYY-MM-DD'),
        to_date:   moment().format('YYYY-MM-DD'),
    });

    const load = useCallback(async (p = 1, pageSize = ps) => {
        setLoading(true);
        try {
            const res = await FleetService.getAuditLog({ ...filters, p, ps: pageSize });
            if (Number(res?.data?.code) === 200) {
                setLogs(res.data.result?.data  || []);
                setTotal(res.data.result?.total || 0);
                setPage(p);
            }
        } catch { message.error('Failed to load audit log'); }
        finally { setLoading(false); }
    }, [filters, ps]);

    useEffect(() => { load(1, ps); }, [filters]);

    const setF = (k, v) => setFilters(p => ({ ...p, [k]: v }));

    const applyPeriod = (period) => {
        setActivePeriod(period.label);
        setFilters(p => ({ ...p, from_date: period.from(), to_date: period.to() }));
    };

    const exportXlsx = async () => {
        setLoading(true);
        try {
            const res  = await FleetService.getAuditLog({ ...filters, p:1, ps:10000 });
            const data = res?.data?.result?.data || [];
            const ws   = XLSX.utils.json_to_sheet(data.map(r => ({
                'ID': r.id, 'Timestamp': fmtDT(r.created_at),
                'User': r.user_name, 'Role': r.user_role,
                'Action': r.action, 'Severity': r.severity,
                'Module': r.module, 'Table': r.table_name,
                'Record ID': r.record_id, 'Description': r.description,
                'IP Address': r.ip_address, 'Region': r.region,
            })));
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Audit Log');
            saveAs(new Blob([XLSX.write(wb,{bookType:'xlsx',type:'array'})]),
                `AuditLog_${moment().format('YYYY-MM-DD_HHmm')}.xlsx`);
        } catch { message.error('Export failed'); }
        finally { setLoading(false); }
    };

    const periodLabel = filters.from_date && filters.to_date
        ? `${moment(filters.from_date).format('DD MMM')} – ${moment(filters.to_date).format('DD MMM YYYY')}`
        : 'All time';

    return (
        <div className="page-content">

            {/* ── Header ─────────────────────────────────────────────────── */}
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:16 }}>
                <div>
                    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                        <LockOutlined style={{ fontSize:20, color:'#1a3a5c' }} />
                        <h4 style={{ margin:0, fontWeight:700 }}>Fleet Compliance Audit Log</h4>
                        <span style={{ fontSize:12, color:'#aaa' }}>{periodLabel}</span>
                    </div>
                    <div style={{ fontSize:12, color:'#888', marginTop:3 }}>
                        {total.toLocaleString()} records · Server-side immutable trail
                        <span style={{ marginLeft:10, color:'#52c41a', fontSize:11 }}>🔒 Append-only</span>
                    </div>
                </div>
                <div style={{ display:'flex', gap:8 }}>
                    <Button icon={<DownloadOutlined />} onClick={exportXlsx} disabled={!logs.length}>Export XLSX</Button>
                    <Button icon={<ReloadOutlined />} onClick={() => load(1,ps)} loading={loading}>Refresh</Button>
                </div>
            </div>

            {/* ── Period presets ──────────────────────────────────────────── */}
            <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:14 }}>
                {PERIODS.map(p => (
                    <button key={p.label} onClick={() => applyPeriod(p)}
                        style={{
                            padding:'4px 12px', borderRadius:16, fontSize:12, cursor:'pointer',
                            border: activePeriod === p.label ? '1.5px solid #1a3a5c' : '1px solid #e8e8e8',
                            background: activePeriod === p.label ? '#1a3a5c' : '#fff',
                            color: activePeriod === p.label ? '#fff' : '#595959',
                            fontWeight: activePeriod === p.label ? 600 : 400,
                            transition: 'all .15s',
                        }}>
                        {p.label}
                    </button>
                ))}
                <div style={{ marginLeft:'auto' }}>
                    <RangePicker size="small"
                        value={[
                            filters.from_date ? moment(filters.from_date) : null,
                            filters.to_date   ? moment(filters.to_date)   : null,
                        ]}
                        onChange={([s,e]=[]) => {
                            setActivePeriod('Custom');
                            setFilters(p => ({
                                ...p,
                                from_date: s ? s.format('YYYY-MM-DD') : '',
                                to_date:   e ? e.format('YYYY-MM-DD') : '',
                            }));
                        }}
                        format="DD MMM YYYY" />
                </div>
            </div>

            {/* ── Mini Dashboard ──────────────────────────────────────────── */}
            {logs.length > 0 && <MiniDashboard logs={logs} total={total} />}

            {/* ── Anomaly Panel ───────────────────────────────────────────── */}
            {logs.length > 0 && <AnomalyPanel logs={logs} />}

            {/* ── Filters row ─────────────────────────────────────────────── */}
            <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:12,
                background:'#fafafa', border:'1px solid #e8e8e8', borderRadius:6, padding:'10px 12px' }}>
                <Input prefix={<SearchOutlined style={{ color:'#ccc' }} />}
                    placeholder="Search description, user, table…"
                    value={filters.k} onChange={e => setF('k', e.target.value)}
                    allowClear style={{ width:240 }} />
                <Select placeholder="Action" value={filters.action||undefined}
                    onChange={v => setF('action', v||'')} allowClear style={{ width:150 }}>
                    {Object.entries(ACTION_CFG).map(([k,v]) => (
                        <Option key={k} value={k}>{v.label}</Option>
                    ))}
                </Select>
                <Select placeholder="Severity" value={filters.severity||undefined}
                    onChange={v => setF('severity', v||'')} allowClear style={{ width:110 }}>
                    {['critical','high','medium','low'].map(s => (
                        <Option key={s} value={s}><SevDot severity={s} /></Option>
                    ))}
                </Select>
                <Select placeholder="Module" value={filters.module||undefined}
                    onChange={v => setF('module', v||'')} allowClear style={{ width:160 }}>
                    {['Fleet/Disposal','Fleet/Incident','Fleet/Assignment','Fleet/Maintenance',
                      'Fleet/TripLog','Fleet/Deployment','Fleet/Document','Users/Role','Users/User','Auth'].map(m => (
                        <Option key={m} value={m}>{m}</Option>
                    ))}
                </Select>
                <Input placeholder="Username" value={filters.user_name}
                    onChange={e => setF('user_name', e.target.value)}
                    allowClear style={{ width:130 }} />
                <div style={{ marginLeft:'auto', fontSize:12, color:'#888', alignSelf:'center' }}>
                    <FilterOutlined style={{ marginRight:4 }} />
                    {logs.length} shown · {total.toLocaleString()} total
                </div>
            </div>

            {/* ── Log Table ───────────────────────────────────────────────── */}
            <Spin spinning={loading}>
                {logs.length === 0 && !loading ? (
                    <div style={{ textAlign:'center', padding:60, color:'#aaa',
                        border:'2px dashed #e8e8e8', borderRadius:8 }}>
                        <LockOutlined style={{ fontSize:40, marginBottom:12, display:'block' }} />
                        No audit records match your filters.
                    </div>
                ) : (
                    <div style={{ border:'1px solid #e8e8e8', borderRadius:8, overflow:'hidden' }}>
                        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                            <thead>
                                <tr style={{ background:'#1a3a5c', color:'#fff' }}>
                                    {['Timestamp','User','Role','Action','Severity','Module','Description','IP',''].map(h => (
                                        <th key={h} style={{ padding:'10px 12px', textAlign:'left',
                                            fontSize:11, fontWeight:600, whiteSpace:'nowrap' }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {logs.map((r, idx) => {
                                    const isCrit = r.severity === 'critical';
                                    return (
                                        <tr key={r.id || idx} onClick={() => setDetail(r)}
                                            style={{
                                                background: isCrit ? '#fff9f9' : idx%2===0 ? '#fff' : '#fafafa',
                                                borderBottom:'1px solid #f0f0f0',
                                                borderLeft:`3px solid ${isCrit ? '#f5222d' : 'transparent'}`,
                                                cursor:'pointer',
                                            }}>
                                            <td style={{ padding:'8px 12px', whiteSpace:'nowrap', fontSize:11, color:'#888' }}>
                                                {fmtDT(r.created_at)}
                                            </td>
                                            <td style={{ padding:'8px 12px' }}>
                                                <div style={{ fontWeight:600, color:'#1a3a5c' }}>{r.user_name}</div>
                                                <div style={{ fontSize:10, color:'#aaa' }}>ID {r.user_id}</div>
                                            </td>
                                            <td style={{ padding:'8px 12px', fontSize:11, color:'#595959' }}>
                                                {r.user_role || '—'}
                                            </td>
                                            <td style={{ padding:'8px 12px' }}>
                                                <ActionBadge action={r.action} />
                                            </td>
                                            <td style={{ padding:'8px 12px' }}>
                                                <SevDot severity={r.severity} />
                                            </td>
                                            <td style={{ padding:'8px 12px' }}>
                                                <span style={{ fontSize:11, background:'#f0f0f0',
                                                    padding:'2px 7px', borderRadius:8, color:'#595959' }}>
                                                    {r.module}
                                                </span>
                                            </td>
                                            <td style={{ padding:'8px 12px', maxWidth:260 }}>
                                                <div style={{ overflow:'hidden', textOverflow:'ellipsis',
                                                    whiteSpace:'nowrap', color:'#333' }}>
                                                    {r.description}
                                                </div>
                                            </td>
                                            <td style={{ padding:'8px 12px', fontSize:11,
                                                color:'#aaa', fontFamily:'monospace' }}>
                                                {r.ip_address || '—'}
                                            </td>
                                            <td style={{ padding:'8px 12px' }}>
                                                <Tooltip title="View details">
                                                    <EyeOutlined style={{ color:'#1a3a5c', cursor:'pointer' }} />
                                                </Tooltip>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>

                        {/* Pagination */}
                        <div style={{ padding:'10px 14px', borderTop:'1px solid #f0f0f0',
                            display:'flex', justifyContent:'space-between', alignItems:'center',
                            background:'#fafafa', fontSize:12 }}>
                            <span style={{ color:'#888' }}>
                                Page {page} · {logs.length} shown · {total.toLocaleString()} total
                            </span>
                            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                                <Select size="small" value={ps}
                                    onChange={v => { setPs(v); load(1,v); }}
                                    style={{ width:90 }}>
                                    {[25,50,100,200].map(n => <Option key={n} value={n}>{n} / page</Option>)}
                                </Select>
                                <Button size="small" disabled={page <= 1}
                                    onClick={() => load(page-1, ps)}>← Prev</Button>
                                <Button size="small" disabled={page * ps >= total}
                                    onClick={() => load(page+1, ps)}>Next →</Button>
                            </div>
                        </div>
                    </div>
                )}
            </Spin>

            <DetailModal record={detail} onClose={() => setDetail(null)} />
        </div>
    );
}